import { isDesktop } from './theme.js';

class MenuDrawerNav extends HTMLElement {
  connectedCallback() {
    this.root = this.querySelector('[data-menu-panel][data-level="0"]');
    this.stack = [];
    this.addEventListener('click', this.onClick);

    this.dialog = this.closest('dialog');
    this.dialog?.addEventListener('close', () => this.reset());
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick = (e) => {
    const opener = e.target.closest('[data-menu-open]');
    if (opener) {
      e.preventDefault();
      this.go(document.getElementById(opener.dataset.menuOpen), 'forward', opener);
      return;
    }
    if (e.target.closest('[data-menu-back]')) {
      e.preventDefault();
      this.back();
    }
  };

  go(panel, direction, opener) {
    if (!panel) return;
    const current = this.current();
    if (current === panel) return;
    if (direction === 'forward') this.stack.push({ panel: current, opener });

    current.hidden = true;
    panel.hidden = false;

    panel.dataset.direction = direction === 'back' ? 'back' : 'forward';

    const target = direction === 'back' ? opener : panel.querySelector('[data-menu-back]');
    target?.focus({ preventScroll: true });
    this.closest('.drawer__body')?.scrollTo({ top: 0 });
  }

  back() {
    const previous = this.stack.pop();
    if (previous) this.go(previous.panel, 'back', previous.opener);
  }

  current() {
    return this.querySelector('[data-menu-panel]:not([hidden])') || this.root;
  }

  reset() {
    this.querySelectorAll('[data-menu-panel]').forEach((panel) => {
      panel.hidden = panel !== this.root;
      delete panel.dataset.direction;
    });
    this.stack = [];
  }
}
customElements.define('menu-drawer-nav', MenuDrawerNav);

class SiteHeader extends HTMLElement {
  connectedCallback() {
    this.wrapper = this.closest('[data-header-wrapper]') || this;
    this.mode = this.dataset.sticky || 'none';
    this.lastY = window.scrollY;
    this.sync();
    this.ro = new ResizeObserver(() => this.sync());
    this.ro.observe(this.wrapper);
    window.addEventListener('scroll', this.onScroll, { passive: true });
    this.setupMenus();
  }

  disconnectedCallback() {
    window.removeEventListener('scroll', this.onScroll);
    if (this.frame) cancelAnimationFrame(this.frame);
    this.ro?.disconnect();
  }

  sync() {
    const y = window.scrollY;
    const height = this.wrapper.offsetHeight;
    const bottom = Math.round(Math.max(0, this.wrapper.getBoundingClientRect().bottom));
    const openMenu = this.mode === 'scroll-up' ? this.querySelector('details[open]') : null;
    const root = document.documentElement.style;

    if (height !== this.height) {
      this.height = height;
      root.setProperty('--header-h', `${height}px`);
      root.setProperty('--header-offset', this.mode === 'none' ? '0px' : `${height}px`);
    }
    if (bottom !== this.bottom) {
      this.bottom = bottom;
      root.setProperty('--header-bottom', `${bottom}px`);
    }
    this.wrapper.classList.toggle('header-wrapper--scrolled', y > 8);
    this.wrapper.classList.toggle(
      'header-wrapper--hidden',
      this.mode === 'scroll-up' && y > height && y > this.lastY && !openMenu
    );
    this.lastY = y;
  }

  onScroll = () => {
    if (this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      this.sync();
    });
  };

  setupMenus() {
    const items = Array.from(this.querySelectorAll('.nav > .nav__item > details'));
    this.menus = items;

    items.forEach((details) => {
      const summary = details.querySelector('summary');

      details.parentElement.addEventListener('pointerenter', (e) => {
        if (e.pointerType !== 'mouse' || !isDesktop() || this.dataset.menuOpen !== 'hover') return;
        clearTimeout(details.leaveTimer);
        const anyOpen = this.menus.some((other) => other.open && !other.hasAttribute('data-closing'));
        if (anyOpen) {
          this.openMenu(details);
        } else {
          clearTimeout(details.enterTimer);
          details.enterTimer = setTimeout(() => this.openMenu(details), 50);
        }
      });

      details.parentElement.addEventListener('pointerleave', (e) => {
        if (e.pointerType !== 'mouse' || this.dataset.menuOpen !== 'hover') return;
        clearTimeout(details.enterTimer);
        clearTimeout(details.leaveTimer);
        details.leaveTimer = setTimeout(() => this.closeMenu(details), 280);
      });

      details.addEventListener('toggle', () => {
        summary.setAttribute('aria-expanded', String(details.open));
        if (details.open) {
          items.forEach((other) => other !== details && this.closeMenu(other, true));
        } else {
          this.settle(details);
          details.removeAttribute('data-swap');
        }
      });
    });

    const nav = this.querySelector('.nav');
    nav?.addEventListener('keydown', (e) => {
      if (!['ArrowLeft', 'ArrowRight'].includes(e.key)) return;
      const tops = Array.from(nav.querySelectorAll(':scope > .nav__item > a, :scope > .nav__item > details > summary'));
      const i = tops.indexOf(document.activeElement);
      if (i === -1) return;
      e.preventDefault();
      const next = tops[(i + (e.key === 'ArrowRight' ? 1 : -1) + tops.length) % tops.length];
      next.focus();
    });
  }

  openMenu(details) {
    clearTimeout(details.enterTimer);
    clearTimeout(details.leaveTimer);
    const closing = details.hasAttribute('data-closing');
    const others = this.menus.some((other) => other !== details && other.open);
    this.settle(details);
    if (details.open && !closing) return;
    details.toggleAttribute('data-swap', closing || others);
    details.open = true;
    details.querySelector('summary')?.setAttribute('aria-expanded', 'true');
  }

  closeMenu(details, instant = false) {
    clearTimeout(details.enterTimer);
    clearTimeout(details.leaveTimer);
    if (!details.open) return;

    const panel = details.querySelector(':scope > .mega, :scope > .nav__dropdown');
    if (instant || !panel || !isDesktop() || matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this.settle(details);
      details.removeAttribute('data-swap');
      details.open = false;
      details.querySelector('summary')?.setAttribute('aria-expanded', 'false');
      return;
    }
    if (details.hasAttribute('data-closing')) return;

    const token = (details.closeToken = (details.closeToken || 0) + 1);
    const finish = (e) => {
      if (e && e.target !== panel) return;
      panel.removeEventListener('animationend', finish);
      if (details.closeToken !== token) return;
      this.closeMenu(details, true);
    };
    details.closeCleanup = () => panel.removeEventListener('animationend', finish);
    details.setAttribute('data-closing', '');
    panel.addEventListener('animationend', finish);
    details.closeTimer = setTimeout(finish, 500);
  }

  settle(details) {
    clearTimeout(details.enterTimer);
    clearTimeout(details.closeTimer);
    details.closeToken = (details.closeToken || 0) + 1;
    details.closeCleanup?.();
    details.closeCleanup = null;
    details.removeAttribute('data-closing');
  }
}
customElements.define('site-header', SiteHeader);

document.addEventListener('shopify:block:select', (e) => {
  const details = e.target.closest('.nav__item > details');
  if (details) details.setAttribute('open', '');
});
